import { z } from 'zod'

import { parseLorebook } from '@/features/studio-renew/lib/import-lorebook'
import { STUDIO_TEXT_LIMITS } from '@/features/studio-renew/lib/studio-text-limits'
import { newRowId } from '@/features/studio-renew/store'
import { MAX_TAG_LENGTH } from '@/features/studio-renew/tags'
import {
  IMPORT_MAX_CHARS,
  parseImport,
} from '@/features/veluvit/services/creator-ai.service'

import type { EditorKind } from '@/features/studio-renew/steps'
import type {
  Character,
  Chat,
  Command,
  StatusBoard,
  World,
} from '@/features/studio-renew/types'

export type ImportKind = EditorKind | 'start'
export type ImportBlock = {
  key: string
  kind: ImportKind
  values: Record<string, string>
}
export const IMPORT_LABEL: Record<ImportKind, string> = {
  character: '캐릭터',
  world: '세계관',
  status: '상태창',
  command: '명령어',
  work: '작품',
  start: '시작 장면',
}
export const IMPORT_FIELDS: Record<ImportKind, Record<string, string>> = {
  character: {
    name: '이름',
    tagline: '한 줄 소개',
    persona: '캐릭터 설정',
    habits: '말버릇',
    forbidden: '거절할 요청',
  },
  world: {
    name: '이름',
    summary: '한 줄 소개',
    body: '세계관 설정',
    places: '장소',
    keywords: '키워드북',
  },
  status: {
    name: '이름',
    fields: '상태 항목',
    html: '상태창 HTML',
    design: '상태창 디자인 설정',
  },
  command: {
    key: '명령어 키',
    title: '화면 이름',
    description: '명령어 설명',
    html: '명령어 HTML',
  },
  work: {
    name: '작품 이름',
    description: '상세 설명',
    genre: '장르',
    tags: '태그',
    target: '타겟',
    leadName: '주인공',
    situation: '상황',
    narrator: '내레이터',
    playGuide: '플레이 가이드',
  },
  start: {
    name: '시작 장면 제목',
    place: '시작 장소',
    context: '숨은 시작 상황',
    speaker: '화자',
    greeting: '첫 대사',
    dialogue: '시작 대화',
    suggestions: '추천 답변',
  },
}
const normalize = (value: string) => value.replace(/\s/g, '').toLowerCase()
const blocks: Record<string, ImportKind> = {
  캐릭터: 'character',
  세계관: 'world',
  상태창: 'status',
  명령어: 'command',
  작품: 'work',
  작품정보: 'work',
  시작장면: 'start',
}
function fieldKey(kind: ImportKind, label: string) {
  return Object.entries(IMPORT_FIELDS[kind]).find(
    ([, title]) => normalize(title) === normalize(label)
  )?.[0]
}

/** 가져오기는 분류만 수행한다. ID 생성·서버 저장은 사용자 검토 후에만 한다. */
export async function parseStudioImport(
  text: string,
  kind: EditorKind
): Promise<{ blocks: ImportBlock[]; warnings: string[] }> {
  if (text.length > IMPORT_MAX_CHARS)
    throw new Error('텍스트는 50,000자까지 가져올 수 있어요.')
  const lines = text.split(/\r?\n/)
  const hasBlocks = lines.some((line) => {
    const match = /^#\s+(.+?)\s*$/.exec(line.trim())
    return !!match && Object.hasOwn(blocks, normalize(match[1]))
  })
  if (!hasBlocks && kind === 'character') {
    const result = await parseImport({ text })
    const character = {
      ...result.character,
      habits: result.character.habits.join('\n'),
      forbidden: result.character.forbidden.join('\n'),
    }
    const resultBlocks: ImportBlock[] = [
      {
        key: 'character:0',
        kind: 'character',
        values: Object.fromEntries(
          Object.entries(character).filter(
            (entry): entry is [string, string] =>
              typeof entry[1] === 'string' && !!entry[1].trim()
          )
        ),
      },
    ]
    if (result.world || result.lorebook)
      resultBlocks.push({
        key: 'world:1',
        kind: 'world',
        values: {
          name: result.world?.name ?? '',
          body: result.world?.body ?? '',
          keywords: result.lorebook ?? '',
        },
      })
    const warnings = result.intro
      ? [
          '시작 상황·첫 대사는 작품의 시작 장면이에요. 작품 가이드로 정리한 뒤 작품 화면에서 가져와 주세요. 원문은 아래에 보관했어요.',
        ]
      : []
    return {
      blocks: resultBlocks.filter((block) =>
        Object.values(block.values).some((value) => value.trim())
      ),
      warnings,
    }
  }
  const result: ImportBlock[] = []
  const warnings: string[] = []
  const markdownFields = lines.some((line) => /^##\s+/.test(line.trim()))
  let current: ImportBlock | null = hasBlocks
    ? null
    : { key: `${kind}:0`, kind, values: {} }
  if (current) result.push(current)
  let field: string | undefined
  for (const raw of lines) {
    const line = raw.trim()
    const heading = /^#\s+(.+?)\s*$/.exec(line)
    if (heading) {
      const label = normalize(heading[1])
      if (Object.hasOwn(blocks, label)) {
        const next = blocks[label]
        current = { key: `${next}:${result.length}`, kind: next, values: {} }
        result.push(current)
        field = undefined
        continue
      }
    }
    if (!current) {
      if (line) warnings.push(`분류되지 않은 내용: ${line.slice(0, 60)}`)
      continue
    }
    const sub = /^##\s+(.+?)\s*$/.exec(line)
    const labeled =
      !hasBlocks && !markdownFields
        ? /^([^:：]+)[:：]\s*(.*)$/.exec(line)
        : null
    const nextField = sub
      ? fieldKey(current.kind, sub[1])
      : labeled
        ? fieldKey(current.kind, labeled[1])
        : undefined
    if (nextField) {
      field = nextField
      current.values[field] ??= ''
      if (labeled?.[2]) current.values[field] += labeled[2]
      continue
    }
    if (sub) {
      warnings.push(
        `${IMPORT_LABEL[current.kind]}의 “${sub[1]}” 항목은 자동 분류하지 못했어요. 원문에서 확인해 주세요.`
      )
      field = undefined
      continue
    }
    if (field)
      current.values[field] =
        (current.values[field] ? `${current.values[field]}\n` : '') + raw
    else if (line) warnings.push(`분류되지 않은 내용: ${line.slice(0, 60)}`)
  }
  return {
    blocks: result
      .map((block) => ({
        ...block,
        values: Object.fromEntries(
          Object.entries(block.values)
            .map(([key, value]) => [key, value.trim()])
            .filter(([, value]) => value)
        ),
      }))
      .filter((block) => Object.keys(block.values).length),
    warnings: [...new Set(warnings)],
  }
}

const linesOf = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
const listOf = (value: string) =>
  value
    .split(/[,，\n]/)
    .map((line) => line.trim())
    .filter(Boolean)
function json<T>(value: string, schema: z.ZodType<T>, label: string): T {
  let raw: unknown
  try {
    raw = JSON.parse(value)
  } catch {
    throw new Error(`${label}의 JSON 배열 형식을 확인해 주세요.`)
  }
  const parsed = schema.safeParse(raw)
  if (!parsed.success)
    throw new Error(`${label}의 항목 형식·길이·개수를 확인해 주세요.`)
  return parsed.data
}
const statusFields = z
  .array(
    z
      .object({
        key: z
          .string()
          .trim()
          .min(1)
          .max(STUDIO_TEXT_LIMITS.statusKey)
          .regex(/^[^{}#/]+$/),
        type: z.enum(['number', 'text', 'list']),
        initial: z.string().max(STUDIO_TEXT_LIMITS.statusInitial),
        min: z.number().finite().optional(),
        max: z.number().finite().optional(),
        options: z
          .array(z.string().trim().min(1).max(STUDIO_TEXT_LIMITS.statusOption))
          .max(8)
          .default([]),
        ordered: z.boolean().optional(),
        hidden: z.boolean().optional(),
        rule: z.string().max(STUDIO_TEXT_LIMITS.statusRule).default(''),
      })
      .strict()
  )
  .max(12)
const places = z
  .array(
    z
      .object({
        name: z.string().trim().min(1).max(STUDIO_TEXT_LIMITS.place),
        note: z.string().max(STUDIO_TEXT_LIMITS.placeNote).default(''),
      })
      .strict()
  )
  .max(20)
export type ImportPatch = {
  character?: Partial<Character>
  world?: Partial<World>
  status?: Partial<StatusBoard>
  command?: Partial<Command>
  work?: Partial<Chat>
  start?: {
    name: string
    place: string
    context: string
    speaker: string
    greeting: string
    suggestions: string[]
  }
  leadName?: string
}

export function importPatch(
  kind: ImportKind,
  values: Record<string, string>
): ImportPatch {
  for (const [key, value] of Object.entries(values)) {
    if (!Object.hasOwn(IMPORT_FIELDS[kind], key))
      throw new Error('지원하지 않는 항목이 있어요.')
    const max =
      key === 'name'
        ? STUDIO_TEXT_LIMITS.name
        : key === 'tagline'
          ? STUDIO_TEXT_LIMITS.tagline
          : key === 'place'
            ? STUDIO_TEXT_LIMITS.place
            : key === 'summary'
              ? STUDIO_TEXT_LIMITS.summary
              : key === 'title'
                ? STUDIO_TEXT_LIMITS.commandTitle
                : key === 'key'
                  ? STUDIO_TEXT_LIMITS.commandKey
                  : null
    if (max && (value.length > max || /\r|\n/.test(value)))
      throw new Error(
        `${IMPORT_FIELDS[kind][key]}은 한 줄 ${max}자 이내로 정리해 주세요.`
      )
  }
  if (kind === 'character') {
    const { habits, forbidden, ...rest } = values
    if (rest.name && (!rest.name.trim() || /[^\p{L}\p{N}\s]/u.test(rest.name)))
      throw new Error(
        '캐릭터 이름은 문자·숫자·공백으로 1자 이상 입력해 주세요.'
      )
    const patch: Partial<Character> = {}
    if (rest.name !== undefined) patch.name = rest.name
    if (rest.tagline !== undefined) patch.tagline = rest.tagline
    if (rest.persona !== undefined) patch.persona = rest.persona
    if (habits !== undefined) {
      const rows = linesOf(habits)
      if (
        rows.length > 5 ||
        rows.some((row) => row.length > STUDIO_TEXT_LIMITS.habit)
      )
        throw new Error('말버릇은 최대 5개, 각 500자예요.')
      patch.habits = rows.map((text) => ({ id: newRowId(), text }))
    }
    if (forbidden !== undefined) {
      patch.forbidden = linesOf(forbidden)
      if (patch.forbidden.length > 30)
        throw new Error('거절할 요청은 최대 30개예요.')
    }
    return { character: patch }
  }
  if (kind === 'world') {
    const { keywords, places: rawPlaces, ...rest } = values
    const patch: Partial<World> = {}
    if (rest.name !== undefined) patch.name = rest.name
    if (rest.summary !== undefined) patch.summary = rest.summary
    if (rest.body !== undefined) patch.body = rest.body
    if (rest.name && rest.name.trim().length < 2)
      throw new Error('세계관 이름은 2자 이상 입력해 주세요.')
    if (keywords !== undefined) {
      const rows = parseLorebook(keywords)
      if (rows.length > 50 || rows.some((row) => row.text.length > 500))
        throw new Error('키워드북은 최대 50개, 본문은 각 500자예요.')
      patch.keywords = rows.map((row) => ({ ...row, id: newRowId() }))
    }
    if (rawPlaces !== undefined)
      patch.places = json(rawPlaces, places, '장소').map((row) => ({
        ...row,
        id: newRowId(),
      }))
    return { world: patch }
  }
  if (kind === 'status') {
    const { fields, ...rest } = values
    const patch: Partial<StatusBoard> = {}
    if (rest.name !== undefined) patch.name = rest.name
    if (rest.html !== undefined) patch.html = rest.html
    if (fields !== undefined) {
      const rows = json(fields, statusFields, '상태 항목')
      if (new Set(rows.map((row) => row.key)).size !== rows.length)
        throw new Error('상태 항목 이름이 중복돼요.')
      for (const row of rows) {
        if (
          row.type === 'list' &&
          (row.options.length < 2 ||
            new Set(row.options).size !== row.options.length ||
            !row.options.includes(row.initial))
        )
          throw new Error(
            '선택형은 서로 다른 선택지 2~8개와 목록에 있는 시작값이 필요해요.'
          )
        if (
          row.type === 'number' &&
          (!row.initial.trim() ||
            !Number.isFinite(Number(row.initial)) ||
            (row.min != null && Number(row.initial) < row.min) ||
            (row.max != null && Number(row.initial) > row.max) ||
            (row.min != null && row.max != null && row.min > row.max))
        )
          throw new Error('숫자 상태 항목의 범위와 시작값을 확인해 주세요.')
      }
      patch.fields = rows.map((row) => ({ ...row, id: newRowId() }))
    }
    return { status: patch }
  }
  if (kind === 'command') {
    if (values.key && !/^![^\s!]{1,49}$/.test(values.key))
      throw new Error('명령어 키는 !로 시작하고 공백 없이 50자 이내예요.')
    const patch: Partial<Command> = {}
    if (values.key !== undefined) patch.key = values.key
    if (values.title !== undefined) patch.title = values.title
    if (values.description !== undefined) patch.description = values.description
    if (values.html !== undefined) patch.html = values.html
    return { command: patch }
  }
  if (kind === 'start') {
    if (!values.name?.trim() || !values.greeting?.trim())
      throw new Error('시작 장면 제목과 첫 대사가 필요해요.')
    const suggestions = linesOf(values.suggestions ?? '')
    if (
      suggestions.length > 3 ||
      suggestions.some((line) => line.length > STUDIO_TEXT_LIMITS.suggestion)
    )
      throw new Error('추천 답변은 최대 3개, 각 300자예요.')
    return {
      start: {
        name: values.name,
        place: values.place ?? '',
        context: values.context ?? '',
        speaker: values.speaker ?? '',
        greeting: values.greeting,
        suggestions,
      },
    }
  }
  const { tags, target, genre, leadName, ...rest } = values
  if (rest.name && rest.name.trim().length < 2)
    throw new Error('작품 이름은 2자 이상 입력해 주세요.')
  const patch: Partial<Chat> = {}
  if (rest.name !== undefined) patch.name = rest.name
  if (rest.description !== undefined) patch.description = rest.description
  if (rest.situation !== undefined) patch.situation = rest.situation
  if (rest.narrator !== undefined) patch.narrator = rest.narrator
  if (rest.playGuide !== undefined) patch.playGuide = rest.playGuide
  if (tags !== undefined) {
    patch.tags = listOf(tags)
    if (patch.tags.length > 10) throw new Error('태그는 최대 10개예요.')
  }
  if (target !== undefined) {
    if (!['전체', '여성향', '남성향'].includes(target))
      throw new Error('타겟은 전체·여성향·남성향 중 하나예요.')
    patch.target = target as Chat['target']
  }
  if (genre !== undefined) {
    if (genre.length > MAX_TAG_LENGTH)
      throw new Error(`장르는 ${MAX_TAG_LENGTH}자 이내로 입력해 주세요.`)
    patch.genre = genre
  }
  return { work: patch, leadName }
}
